/* eslint-disable react/jsx-curly-newline */
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetOverlay } from '@frontend/shadcn/components/ui/sheet';
import { Alert, AlertDescription } from '@frontend/shadcn/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/shadcn/components/ui/dialog';
import { XmarkIcon, PencilIcon, CheckIcon, BrowserIcon, CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button, buttonVariants } from '@frontend/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import dayjs from 'dayjs';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@frontend/shadcn/components/ui/tabs';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import {
  ArrowUpRightFromSquareIcon,
  ChartBarIcon,
  ChevronRightIcon,
  PlusIcon,
} from '@revfluence/fresh-icons/regular/esm';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@frontend/shadcn/components/ui/form';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { COUNTRY_DATA } from '@frontend/app/constants/countryData';
import { MultiSelect } from '@frontend/app/components/MultiSelect/MultiSelect';
import { FieldErrors, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { logger } from '@common';
import { MetaIcon } from '@revfluence/fresh-icons/brands/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import { FolderBookmarkIcon, Grid2Icon } from '@revfluence/fresh-icons/solid';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Link } from 'react-router-dom';
import { SOCIAL_POST_APP_ID } from '@frontend/app/constants/applicationIds';
import type { InstagramAccount, MetaAd, MetaAdAccount, MetaAdCampaign, MetaAdSet } from '@frontend/gql/social/graphql';

import { useImageDownloader, useMessagingContext } from '@frontend/hooks';
import { message } from '@revfluence/fresh';

import { UserIcon } from '@revfluence/fresh-icons/regular/esm';

import { ContentPreviewCard } from './components/content-preview-card';
import { MetricCard, MetricCardLabel, MetricCardValue } from './components/metric-card';
import { PerformanceHeader, PerformanceTitle, PerformanceDateRange } from './components/performance-header';
import {
  SheetGrid,
  SheetTopBar,
  SheetColumn,
  PreviewSheetContent,
  PreviewSheetLabel,
  PreviewSheetSection,
  PreviewSheetBadgeContainer,
  PreviewSheetBadge,
  PreviewSheetEmpty,
  MetricsGrid,
  SheetHeaderWithClose,
  SheetTopBarPlatformInfo,
  SheetTopBarDateBadge,
} from '@/shadcn/components/sheet-extensions';

import ContentPreviewAlert, { IAlertProps } from './components/content-preview-alert';

import { boostPostSchema } from './types/boost-post-schema';
import type { BoostPostFormValues } from './types/boost-post-schema';

import { type ContentPreviewData, type ContentPreviewSheetProps as BaseContentPreviewSheetProps } from './types';

import { GenericUtmParametersGroup, resolveUtmParameters, buildUrlWithUtm } from './components/utm';
import { DYNAMIC_UTM_OPTIONS } from './utils/utmUtils';
import { convertToTitleCase } from '../../../../app/utils/strings';
import { transformToPostInsights } from './utils/transform-to-post-insights';
import { ProfileSection, ProfileAvatar, ProfileHandle, ProfileMetadataRow } from '@/shadcn/components/profile';
import { SheetTopBarBoostActionMenu } from './components/sheet-top-bar';
import { handleExternalLink, handleDownload } from './utils/content-actions';
import { toLocalDatetimeInputValue } from '@/app/utils/date';

// Define the expected shape of the utmParameters object from the form
interface StandardUtmParamsForm {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

// Define the expected shape of a custom UTM parameter from the form
interface CustomUtmParamForm {
  key?: string;
  value?: string;
}

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

// Function to get badge colors based on ad status
const getStatusBadgeColors = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
    case 'PREAPPROVED':
      return 'bg-green-500 text-white';
    case 'PAUSED':
    case 'ADSET_PAUSED':
    case 'CAMPAIGN_PAUSED':
      return 'bg-yellow-500 text-white';
    case 'IN_PROCESS':
    case 'PENDING_REVIEW':
    case 'PENDING_BILLING_INFO':
      return 'bg-blue-500 text-white';
    case 'DISAPPROVED':
    case 'WITH_ISSUES':
      return 'bg-red-500 text-white';
    case 'ARCHIVED':
    case 'DELETED':
      return 'bg-gray-500 text-white';
    default:
      return 'bg-gray-400 text-white';
  }
};

interface ContentPreviewSheetProps extends Omit<BaseContentPreviewSheetProps, 'content'> {
  content:
    | (ContentPreviewData & {
        onEditProject?: () => void;
        onEditGroup?: () => void;
        onEditMusic?: () => void;
        onEditRetailer?: () => void;
        onEditBrands?: () => void;
      })
    | null;
  isLoading?: boolean;
}

const useBoost = (content: ContentPreviewData | null, availableMetaAdAccounts?: { id: string }[]) => {
  const boostPostForm = useForm<BoostPostFormValues>({
    resolver: zodResolver(boostPostSchema),
    defaultValues: {
      campaign: { type: 'existing' },
      adSet: {
        type: 'existing',
      },
      post: {
        postExternalId: '', // Initialize empty, will be set in useEffect
        platform: undefined, // Initialize undefined, will be set in useEffect
      },
      ad: {
        callToActionType: 'NO_BUTTON', // EPD-1644: Default to NO_BUTTON for callToActionType
        callToActionUrl: undefined, // EPD-1644: Explicitly set to undefined for NO_BUTTON default
        status: 'ACTIVE', // EPD-1555: Default ad status to ACTIVE for publishing
        useUtmParameters: false,
        utmParameters: {
          utmSource: 'facebook',
          utmMedium: 'cpc',
          utmCampaign: '{{adset.name}}',
          utmContent: '{{ad.name}}',
        },
        customUtmParameters: [],
      },
      // ...other default values
    },
    mode: 'onTouched',
  });
  const campaignType = boostPostForm.watch('campaign.type');
  const campaignId = boostPostForm.watch('campaign.campaignId');
  const campaignName = boostPostForm.watch('campaign.name');
  const campaignObjective = boostPostForm.watch('campaign.objective');
  const adSetType = boostPostForm.watch('adSet.type');

  // EPD-1486: Auto-switch to new ad set when creating new campaign
  React.useEffect(() => {
    if (campaignType === 'new' && adSetType === 'existing') {
      boostPostForm.setValue('adSet.type', 'new');
      // EPD-1664: Set default status to ACTIVE when switching to new ad set
      boostPostForm.setValue('adSet.status', 'ACTIVE');
    }
  }, [campaignType, adSetType, boostPostForm]);

  // EPD-1493: Set default objective to "Awareness" when switching to new campaign
  React.useEffect(() => {
    if (campaignType === 'new' && !campaignObjective) {
      boostPostForm.setValue('campaign.objective', 'OUTCOME_AWARENESS');
    }
  }, [campaignType, campaignObjective, boostPostForm]);

  // EPD-1664: Set default status to "ACTIVE" when switching to new campaign
  React.useEffect(() => {
    if (campaignType === 'new') {
      boostPostForm.setValue('campaign.status', 'ACTIVE');
    }
  }, [campaignType, boostPostForm]);

  // EPD-1492: Reset campaign and ad set when ad account changes
  const selectedAdAccountId = boostPostForm.watch('adAccount.id');
  const previousAdAccountIdRef = React.useRef<string | undefined>();

  React.useEffect(() => {
    // Only reset if the ad account actually changed (not initial load)
    if (
      previousAdAccountIdRef.current &&
      selectedAdAccountId &&
      previousAdAccountIdRef.current !== selectedAdAccountId
    ) {
      // Reset campaign to default state
      boostPostForm.setValue('campaign.type', 'existing');
      boostPostForm.setValue('campaign.campaignId', '');
      boostPostForm.setValue('campaign.name', '');
      boostPostForm.setValue('campaign.objective', undefined as any);

      // Reset ad set to default state
      boostPostForm.setValue('adSet.type', 'existing');
      boostPostForm.setValue('adSet.id', '');
      boostPostForm.setValue('adSet.name', '');

      // Clear ad set form fields
      boostPostForm.setValue('adSet.dailyBudget', undefined);
      boostPostForm.setValue('adSet.lifetimeBudget', undefined);
      boostPostForm.setValue('adSet.bidAmount', undefined);
      boostPostForm.setValue('adSet.billingEvent', undefined);
      boostPostForm.setValue('adSet.startTime', undefined);
      boostPostForm.setValue('adSet.stopTime', undefined);

      // Reset targeting back to default (US)
      boostPostForm.setValue('adSet.targeting.geo_locations.countries', ['US']);
    }

    // Update the ref for next comparison
    previousAdAccountIdRef.current = selectedAdAccountId;
  }, [selectedAdAccountId, boostPostForm]);

  // Update form values when content data becomes available
  React.useEffect(() => {
    if (content?.postExternalId) {
      const currentPostExternalId = boostPostForm.getValues('post.postExternalId');
      if (currentPostExternalId !== content.postExternalId) {
        boostPostForm.setValue('post.postExternalId', content.postExternalId);
      }
    }
  }, [content?.postExternalId, boostPostForm]);

  // Update platform when content metadata becomes available
  React.useEffect(() => {
    if (content?.metadata?.platform) {
      const currentPlatform = boostPostForm.getValues('post.platform');
      // Only set platform if it's instagram, otherwise leave it undefined
      const newPlatform = content.metadata.platform === 'instagram' ? ('instagram' as const) : undefined;
      if (currentPlatform !== newPlatform && newPlatform !== undefined) {
        boostPostForm.setValue('post.platform', newPlatform);
      }
    }
  }, [content?.metadata?.platform, boostPostForm]);

  // Set default ad account to the first available
  React.useEffect(() => {
    if (availableMetaAdAccounts && availableMetaAdAccounts.length > 0 && !boostPostForm.getValues('adAccount.id')) {
      boostPostForm.setValue('adAccount.id', availableMetaAdAccounts[0].id);
    }
  }, [availableMetaAdAccounts, boostPostForm]);

  // Set default US targeting when switching to new adSet type
  React.useEffect(() => {
    if (adSetType === 'new') {
      const currentTargeting = boostPostForm.getValues('adSet.targeting');
      if (!currentTargeting?.geo_locations?.countries?.length) {
        boostPostForm.setValue('adSet.targeting.geo_locations.countries', ['US']);
      }
    }
  }, [adSetType, boostPostForm]);

  // EPD-1644: Clear URL fields when NO_BUTTON is selected
  const callToActionType = boostPostForm.watch('ad.callToActionType');
  React.useEffect(() => {
    if (callToActionType === 'NO_BUTTON') {
      boostPostForm.setValue('ad.callToActionUrl', undefined);
      boostPostForm.setValue('ad.useUtmParameters', false);
    }
  }, [callToActionType, boostPostForm]);

  const { formState } = boostPostForm;
  const { errors } = formState;
  const isCampaignValid =
    campaignType === 'existing'
      ? !!campaignId && !errors.campaign
      : !!campaignName && !!campaignObjective && !errors.campaign;

  return { boostPostForm, isCampaignValid };
};

const CAMPAIGN_OBJECTIVES = [
  { value: 'OUTCOME_APP_PROMOTION', label: 'App Promotion' },
  { value: 'OUTCOME_AWARENESS', label: 'Awareness' },
  { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement' },
  { value: 'OUTCOME_LEADS', label: 'Leads' },
  { value: 'OUTCOME_SALES', label: 'Sales' },
  { value: 'OUTCOME_TRAFFIC', label: 'Traffic' },
];

function contentPreviewAlertProps(
  advertisableMediaPermissions?: ContentPreviewData['advertisableMediaPermissions'],
  eligibilityErrors?: string[],
  contentPlatform?: string,
  postExternalId?: string,
  availableMetaAdAccounts?: MetaAdAccount[],
  availableInstagramAccounts?: InstagramAccount[],
): IAlertProps & { canBoost: boolean } {
  const hasPermissions = advertisableMediaPermissions?.some((permission) => permission.hasPermissions);
  const hasEligibilityErrors = eligibilityErrors && eligibilityErrors.length > 0;
  const isInstagramPost = contentPlatform === 'instagram';
  const hasPostExternalId = !!postExternalId;
  const hasMetaAdAccounts = availableMetaAdAccounts && availableMetaAdAccounts.length > 0;
  const hasInstagramAccounts = availableInstagramAccounts && availableInstagramAccounts.length > 0;

  // Case 0: Still loading permissions data
  if (advertisableMediaPermissions === undefined) {
    return {
      type: 'info' as const,
      canBoost: false, // Disable boost button while loading
      children: (
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
          <span>Checking boost permissions...</span>
        </div>
      ),
    };
  }

  // Case 0.1: Server error occurred (null response)
  if (advertisableMediaPermissions === null) {
    return {
      type: 'error' as const,
      canBoost: false,
      children: (
        <span>
          Unable to check boost permissions at this time. Please try again later or contact support if the issue
          persists.
        </span>
      ),
    };
  }

  if (!hasMetaAdAccounts) {
    return {
      type: 'error' as const,
      canBoost: false,
      children: (
        <span>No Meta Ad Accounts with valid authentication. Please connect your Meta Ad Account in the settings.</span>
      ),
    };
  }

  if (!hasInstagramAccounts) {
    return {
      type: 'error' as const,
      canBoost: false,
      children: (
        <span>
          No Instagram Accounts with valid authentication. Please connect your Instagram Account in the settings.
        </span>
      ),
    };
  }

  // Case 0.5: Loaded but no permissions available (empty array or all permissions are false)
  if (advertisableMediaPermissions.length === 0 || !hasPermissions) {
    // If it's not an Instagram post, show platform error instead
    if (!isInstagramPost) {
      return {
        type: 'error' as const,
        canBoost: false,
        children: (
          <span>Only Instagram posts can be boosted. This post is from {contentPlatform || 'another platform'}.</span>
        ),
      };
    }

    // If missing postExternalId, show that error instead
    if (!hasPostExternalId) {
      return {
        type: 'error' as const,
        canBoost: false,
        children: (
          <span>
            This post cannot be boosted because it's missing required Instagram post data. Please ensure the post is
            properly synced from Instagram.
          </span>
        ),
      };
    }

    // Show no permissions state
    return {
      type: 'unAuthorized' as const,
      canBoost: true,
      children: (
        <span>
          You don't have permission to boost this post on Instagram.
          <br />
          If you proceed to boost the post, we will attempt to contact the creator on your behalf to obtain the
          necessary permission and then proceed with your ad creation request.
        </span>
      ),
    };
  }

  // At this point, we know permissions are loaded and user has some permissions
  // EPD-1494: Platform validation - Only Instagram posts can be boosted
  if (!isInstagramPost) {
    return {
      type: 'error' as const,
      canBoost: false,
      children: (
        <span>Only Instagram posts can be boosted. This post is from {contentPlatform || 'another platform'}.</span>
      ),
    };
  }

  // EPD-1498: Validate postExternalId is present
  if (!hasPostExternalId) {
    return {
      type: 'error' as const,
      canBoost: false,
      children: (
        <span>
          This post cannot be boosted because it's missing required Instagram post data. Please ensure the post is
          properly synced from Instagram.
        </span>
      ),
    };
  }

  // Case 1: Has permission and content is eligible
  if (!hasEligibilityErrors) {
    return {
      type: 'success' as const,
      canBoost: true,
      children: <span>You are authorized to boost this post on Instagram</span>,
    };
  }

  // Case 2: Has permission but content is not eligible
  return {
    type: 'error' as const,
    canBoost: false,
    children: (
      <div>
        {eligibilityErrors.length === 1 ? (
          <span>{eligibilityErrors[0]}</span>
        ) : (
          <div>
            <div className="mb-2">This content is not eligible for boosting:</div>
            <ul className="list-disc list-inside space-y-1">
              {eligibilityErrors.map((error, index) => (
                <li key={index} className="text-sm">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    ),
  };
}

// Add the inline success notification component using shadcn Alert
interface InlineSuccessNotificationProps {
  message: string;
  onDismiss: () => void;
}

const InlineSuccessNotification: React.FC<InlineSuccessNotificationProps> = ({ message, onDismiss }) => (
  <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-2">
        <CheckIcon className="h-4 w-4 text-green-600" />
        <AlertDescription className="font-medium">{message}</AlertDescription>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={onDismiss}
        className="h-6 w-6 p-0 text-green-600 hover:text-green-800 hover:bg-green-100"
        aria-label="Dismiss notification"
      >
        <XmarkIcon className="h-4 w-4" />
      </Button>
    </div>
  </Alert>
);

export function ContentPreviewSheetBoost({
  content,
  open,
  onOpenChange,
  availableProjects,
  availableGroups,
  availableMetaAdAccounts,
  availableInstagramAccounts,
  children,
  onBoostPost,
  isLoading = false,
}: Readonly<ContentPreviewSheetProps>) {
  const { boostPostForm, isCampaignValid } = useBoost(content, availableMetaAdAccounts);
  const [boostView, setBoostView] = useState<'view' | 'create'>('view');
  const [selectedBudgetType, setSelectedBudgetType] = useState<'lifetime' | 'daily'>('daily');
  const [hasEndDate, setHasEndDate] = useState<boolean>(false);
  const [isBoostLoading, setIsBoostLoading] = useState<boolean>(false);

  // Add state for inline success notification
  const [inlineSuccessMessage, setInlineSuccessMessage] = useState<string>('');

  // Add state for publish confirmation dialog
  const [showPublishConfirmation, setShowPublishConfirmation] = useState<boolean>(false);

  // Ref to track timeout for preventing memory leaks
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Add state for showing success message in view mode
  const [viewModeSuccessMessage, setViewModeSuccessMessage] = useState<string>('');

  const { showSuccessMessage, showErrorMessage } = useMessagingContext();
  const { downloadImages } = useImageDownloader({
    onProgress: (progress: number) => {
      message.info(`Download progress: ${Math.round(progress * 100)}%`);
    },
  });

  const handleDownloadPosts = useCallback(async () => {
    if (!content?.originalUrl) {
      showErrorMessage('No download URL available for this post');
      return;
    }

    try {
      const timestamp = new Date().toISOString();
      const filename = `social_posts_${timestamp}`;

      const downloadItems = [
        {
          entityId: content.id,
          postPermalink: content.originalUrl,
        },
      ];

      const result = await downloadImages(downloadItems, filename);

      if (result.failed.length > 0) {
        showErrorMessage(`${result.successful.length} images downloaded successfully, ${result.failed.length} failed`);
      } else {
        showSuccessMessage('Post downloaded successfully');
      }
    } catch (error) {
      showErrorMessage(`Failed to download images: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [content?.id, content?.originalUrl, downloadImages, showSuccessMessage, showErrorMessage]);

  const handleActionMenuClick = (action: string) => {
    switch (action) {
      case 'download-posts':
        handleDownloadPosts();
        break;
      case 'view-post':
        if (content?.originalUrl) {
          window.open(content.originalUrl, '_blank', 'noopener,noreferrer');
        } else {
          showErrorMessage('No external URL available for this post');
        }
        break;
      case 'create-boost':
        // Switch to Boost tab and open create view
        setBoostView('create');
        // Optionally scroll to Boost tab (not controlled programmatically here)
        break;
    }
  };

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
  }, []);

  // Clear view mode success message when component unmounts or dialog closes
  useEffect(() => {
    if (!open) {
      setViewModeSuccessMessage('');
    }
  }, [open]);

  const campaignType = boostPostForm.watch('campaign.type');
  const campaignId = boostPostForm.watch('campaign.campaignId');
  const adSetType = boostPostForm.watch('adSet.type');
  const selectedAdAccountId = boostPostForm.watch('adAccount.id');
  const selectedAdAccount = availableMetaAdAccounts?.find((metaAdAccount) => metaAdAccount.id === selectedAdAccountId);
  const availableAdCampaigns = selectedAdAccount?.metaAdCampaigns;
  const selectedAdCampaign = availableAdCampaigns?.find((adCampaign) => adCampaign?.id === campaignId);
  const availableAdSets = selectedAdCampaign?.metaAdSets;

  // Initialize hasEndDate based on existing stopTime value
  useEffect(() => {
    const currentStopTime = boostPostForm.getValues('adSet.stopTime');
    setHasEndDate(!!currentStopTime);
  }, [boostPostForm]);

  // Watch UTM parameters for live URL preview
  const utmParameters = useWatch({
    control: boostPostForm.control,
    name: 'ad.utmParameters',
  }) as StandardUtmParamsForm | undefined;

  const customUtmParameters = useWatch({
    control: boostPostForm.control,
    name: 'ad.customUtmParameters',
  }) as CustomUtmParamForm[] | undefined;

  const useCustomUtm = useWatch({ control: boostPostForm.control, name: 'ad.useUtmParameters' });
  const callToActionUrlFromForm = boostPostForm.watch('ad.callToActionUrl'); // Watch the CtoA URL
  const callToActionType = boostPostForm.watch('ad.callToActionType'); // Watch the CtoA type for NO_BUTTON logic

  // Watch form values for dynamic UTM resolution
  const adName = boostPostForm.watch('ad.name');
  const adSetName = boostPostForm.watch('adSet.name');
  const selectedAdSetId = boostPostForm.watch('adSet.id');

  // Get the selected adset name if using existing adset
  const selectedAdSetFromData = availableAdSets
    ?.filter((adSet): adSet is NonNullable<typeof adSet> => !!adSet)
    ?.find((adSet) => adSet.id === selectedAdSetId);
  const effectiveAdSetName = adSetName || selectedAdSetFromData?.name || '';

  const previewUrl = useMemo(() => {
    // EPD-1644: If NO_BUTTON is selected, no URL is needed
    if (callToActionType === 'NO_BUTTON') {
      return 'No destination URL required for "No Button" call-to-action.';
    }

    let rawBaseLink = '';

    // Determine the initial base link based on whether UTMs are used and CtoA URL is present
    if (useCustomUtm && callToActionUrlFromForm) {
      rawBaseLink = callToActionUrlFromForm.trim(); // Trim whitespace
    } else if (content.originalUrl) {
      // Fallback if UTMs are off OR CtoA is empty
      rawBaseLink = content.originalUrl;
    }

    // If UTMs are disabled, the preview is just the raw base link (or a message if none)
    if (!useCustomUtm) {
      return rawBaseLink || 'UTM parameters disabled and no URL provided.';
    }

    // If UTMs are enabled, but there's no Call To Action URL from the form, prompt user
    const trimmedCallToActionUrl = callToActionUrlFromForm?.trim();
    if (!trimmedCallToActionUrl) {
      return 'Enter a destination URL to see the preview with UTMs.';
    }

    // Validate and normalize the URL using URL constructor with try-catch
    let validBaseLink = '';

    // Helper function to validate URL
    const isValidUrl = (urlString: string): boolean => {
      try {
        return Boolean(new URL(urlString));
      } catch {
        return false;
      }
    };

    if (isValidUrl(trimmedCallToActionUrl)) {
      validBaseLink = trimmedCallToActionUrl;
    } else if (isValidUrl(`https://${trimmedCallToActionUrl}`)) {
      validBaseLink = `https://${trimmedCallToActionUrl}`;
    } else {
      return 'Invalid URL format. Please enter a valid URL.';
    }

    const watchedStandardParams = utmParameters || {};
    const validCustomParams = (customUtmParameters || []).filter(
      (param): param is { key: string; value: string } =>
        !!param.key && !!param.key.trim() && !!param.value && !!param.value.trim(),
    );

    const { utmParameters: resolvedStandardUtms, customParameters: resolvedCustomUtms } = resolveUtmParameters(
      watchedStandardParams,
      validCustomParams,
      content,
      { adName, adsetName: effectiveAdSetName }, // Pass ad and adset context
    );

    try {
      return buildUrlWithUtm(validBaseLink, resolvedStandardUtms, resolvedCustomUtms);
    } catch (e) {
      return 'Error building URL with UTM parameters.';
    }
  }, [
    useCustomUtm,
    utmParameters,
    customUtmParameters,
    content,
    callToActionUrlFromForm,
    adName,
    effectiveAdSetName,
    callToActionType,
  ]);

  const onBoostPostSubmit = async (values: BoostPostFormValues) => {
    if (isBoostLoading) return; // Prevent double submission

    setIsBoostLoading(true);
    setShowPublishConfirmation(false); // Close confirmation dialog
    // Clear any existing inline success message
    setInlineSuccessMessage('');
    // Clear any existing view mode success message
    setViewModeSuccessMessage('');

    try {
      logger.info('Boost post form submitted:', values);

      // Create a mutable copy for submission using structuredClone to preserve Date objects
      // Fallback to shallow clone if structuredClone is not available (preserves Date refs but doesn't deep clone)
      const submissionValues: BoostPostFormValues =
        typeof structuredClone !== 'undefined' ? structuredClone(values) : { ...values };

      // EPD-1555: Ensure ad status is set to ACTIVE for publishing
      submissionValues.ad.status = 'ACTIVE';

      // EPD-1644: Handle NO_BUTTON case
      if (submissionValues.ad.callToActionType === 'NO_BUTTON') {
        submissionValues.ad.callToActionUrl = undefined;
        submissionValues.ad.useUtmParameters = false;
        // Also clear UTM parameters when NO_BUTTON is selected
        submissionValues.ad.utmParameters = undefined;
        submissionValues.ad.customUtmParameters = undefined;
      }

      // Debug logging to check what's being sent to server
      logger.info('EPD-1644 Debug - Final submission values:', {
        callToActionType: submissionValues.ad.callToActionType,
        callToActionUrl: submissionValues.ad.callToActionUrl,
        isPlaceholderUrl:
          submissionValues.ad.callToActionType === 'NO_BUTTON' &&
          submissionValues.ad.callToActionUrl === 'https://example.com/no-button-placeholder',
      });

      // EPD-1644: Skip URL processing when NO_BUTTON is selected
      if (
        submissionValues.ad.callToActionType !== 'NO_BUTTON' &&
        submissionValues.ad.useUtmParameters &&
        submissionValues.ad.callToActionUrl
      ) {
        let baseUrl = submissionValues.ad.callToActionUrl;

        // Ensure base URL has a scheme for URL constructor and utmUtils
        if (!/^https?:\/\//i.test(baseUrl)) {
          baseUrl = `https://${baseUrl}`;
        }

        try {
          // 1. Resolve dynamic parameters using the 'content' object
          const validCustomUtmParameters = (submissionValues.ad.customUtmParameters || []).filter(
            (param) => param.key && param.key.trim() !== '' && param.value && param.value.trim() !== '',
          ) as { key: string; value: string }[]; // Type assertion after filtering

          // Get ad and adset names for dynamic resolution
          const submissionAdName = submissionValues.ad.name || '';
          const submissionAdSetName =
            submissionValues.adSet.type === 'new'
              ? submissionValues.adSet.name || ''
              : submissionValues.adSet.type === 'existing'
                ? availableAdSets?.find(
                    (adSet) => adSet?.id === (submissionValues.adSet as { type: 'existing'; id: string }).id,
                  )?.name || ''
                : '';

          const resolvedParams = resolveUtmParameters(
            submissionValues.ad.utmParameters || {}, // Provide default empty object
            validCustomUtmParameters, // Pass the filtered and asserted array
            content, // The content object with actual values
            { adName: submissionAdName, adsetName: submissionAdSetName }, // Pass ad and adset context
          );

          // 2. Build the final URL with the now-resolved parameters
          const finalUrl = buildUrlWithUtm(
            baseUrl, // The base Call To Action URL
            resolvedParams.utmParameters, // Resolved standard UTMs
            resolvedParams.customParameters, // Resolved custom UTMs
          );

          // 3. Update the callToActionUrl in the submission data
          submissionValues.ad.callToActionUrl = finalUrl;

          // Optional: Clear out the separate UTM parameter fields if the backend
          // only expects the final composed URL and not the individual UTM fields.
          // submissionValues.ad.utmParameters = undefined;
          // submissionValues.ad.customUtmParameters = undefined;
        } catch (error) {
          logger.error('Error constructing final URL for submission:', error);
          // Potentially add a user-facing error or prevent submission
          // For now, we'll log and proceed with the URL as it was (which might be partially processed by Zod)
          // Or, if the error is critical, you could do:
          // boostPostForm.setError('ad.callToActionUrl', { type: 'manual', message: 'Failed to build final URL with UTMs.' });
          // return; // Stop submission
        }
      }

      // Call the onBoostPost prop with submission values and form setValue function
      if (onBoostPost) {
        // Create a wrapper function to match the expected signature
        const setFormValueWrapper = (name: string, value: unknown) => {
          logger.info('🔍 DEBUG - setFormValueWrapper called with:', { name, value });

          // Add debugging for dropdown options
          if (name.includes('campaign')) {
            const currentAdAccountId = boostPostForm.getValues('adAccount.id');
            const selectedAccount = availableMetaAdAccounts?.find((acc) => acc.id === currentAdAccountId);
            logger.info('🔍 DEBUG - Campaign field update - current dropdown state:', {
              currentAdAccountId,
              hasSelectedAccount: !!selectedAccount,
              availableCampaigns:
                selectedAccount?.metaAdCampaigns
                  ?.filter((c): c is NonNullable<typeof c> => !!c)
                  ?.map((c) => ({ id: c.id, name: c.name })) || [],
              availableCampaignCount: selectedAccount?.metaAdCampaigns?.length || 0,
            });
          }

          if (name.includes('adSet')) {
            const currentAdAccountId = boostPostForm.getValues('adAccount.id');
            const currentCampaignId = boostPostForm.getValues('campaign.campaignId');
            const selectedAccount = availableMetaAdAccounts?.find((acc) => acc.id === currentAdAccountId);
            const selectedCampaign = selectedAccount?.metaAdCampaigns
              ?.filter((c): c is NonNullable<typeof c> => !!c)
              ?.find((c) => c.id === currentCampaignId);
            logger.info('🔍 DEBUG - Ad Set field update - current dropdown state:', {
              currentAdAccountId,
              currentCampaignId,
              hasSelectedAccount: !!selectedAccount,
              hasSelectedCampaign: !!selectedCampaign,
              availableAdSets:
                selectedCampaign?.metaAdSets
                  ?.filter((as): as is NonNullable<typeof as> => !!as)
                  ?.map((as) => ({ id: as.id, name: as.name })) || [],
              availableAdSetCount: selectedCampaign?.metaAdSets?.length || 0,
            });
          }

          try {
            // Cast to any to bypass strict typing - this is safe because we control the form structure
            (boostPostForm.setValue as (name: string, value: unknown, options?: object) => void)(name, value, {
              shouldDirty: true,
              shouldValidate: true,
              shouldTouch: true,
            });

            // Verify the value was actually set
            const currentValue = boostPostForm.getValues(name as keyof BoostPostFormValues);
            logger.info('🔍 DEBUG - Form value after setting:', {
              name,
              expectedValue: value,
              actualValue: currentValue,
              success: currentValue === value,
            });

            // Additional debugging for dropdown-related fields
            if (name.includes('campaign') || name.includes('adSet')) {
              const allFormValues = boostPostForm.getValues();
              logger.info('🔍 DEBUG - All form values after update:', {
                adAccount: allFormValues.adAccount,
                campaign: allFormValues.campaign,
                adSet: allFormValues.adSet,
              });
            }
          } catch (error) {
            logger.error('🔍 DEBUG - Error setting form value:', { name, value, error });
          }
        };

        // Call the original onBoostPost function
        const result = await onBoostPost(submissionValues, setFormValueWrapper);

        // Check if the boost post service actually succeeded
        if (result && typeof result === 'object' && 'success' in result && result.success) {
          // EPD-1676: Switch to view mode immediately and show success message there
          const successMessage =
            'successMessage' in result && result.successMessage ? result.successMessage : 'Ad published successfully';

          // Switch to view mode immediately
          setBoostView('view');
          // Show success message in view mode
          setViewModeSuccessMessage(successMessage);

          // Clear the success message after 5 seconds
          if (successTimeoutRef.current) {
            clearTimeout(successTimeoutRef.current);
          }
          successTimeoutRef.current = setTimeout(() => {
            setViewModeSuccessMessage('');
          }, 5000);
        } else {
          // Handle failure case - don't set success message
          logger.warn('Boost post failed or returned invalid result:', result);
          // Clear any existing success message
          setInlineSuccessMessage('');
          // Clear any pending timeout since we're not showing success
          if (successTimeoutRef.current) {
            clearTimeout(successTimeoutRef.current);
            successTimeoutRef.current = null;
          }
          // Error handling is managed in the onBoostPost function in PostsTab.tsx
        }
      } else {
        // Fallback if no onBoostPost prop is provided
        logger.warn('No onBoostPost handler provided');
      }
    } catch (error) {
      logger.error('Error during boost post submission:', error);
      // Clear any success message on error
      setInlineSuccessMessage('');
      // Clear any pending timeout since we encountered an error
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
      // The error handling is now managed in the onBoostPost function in PostsTab.tsx
    } finally {
      setIsBoostLoading(false);
    }
  };

  const onBoostPostError = (errors: FieldErrors<BoostPostFormValues>) => {
    logger.error('Boost Post Form Errors:', errors);
  };

  const onBoostPostCancel = () => {
    logger.info('Boost Post Form Canceled');
    boostPostForm.reset();
    setBoostView('view');
    // Clear any existing success message when canceling
    setViewModeSuccessMessage('');
  };

  const metaAdAccountOptions =
    availableMetaAdAccounts?.map((metaAdAccount: { id: string; name: string }) => ({
      label: metaAdAccount.name,
      value: metaAdAccount.id,
    })) ?? [];

  const campaignOptions =
    availableAdCampaigns
      ?.filter(
        (adCampaign): adCampaign is NonNullable<typeof adCampaign> =>
          !!adCampaign && !!adCampaign.name && !!adCampaign.id,
      )
      ?.map((adCampaign) => ({
        label: adCampaign.name,
        value: adCampaign.id,
      })) ?? [];

  const projectOptions =
    availableProjects?.map((p: { name: string; id: string }) => ({
      label: p.name,
      value: p.id,
    })) ?? [];

  const groupOptions =
    availableGroups?.map((g: { name: string; id: string }) => ({
      label: g.name,
      value: g.id,
    })) ?? [];

  const currentAds = useMemo(() => {
    const currentAdsWithContext: Array<{
      ad: MetaAd;
      adSet: MetaAdSet;
      campaign: MetaAdCampaign;
    }> = [];

    availableMetaAdAccounts?.forEach((adAccount) => {
      adAccount.metaAdCampaigns?.forEach((campaign) => {
        if (!campaign) return;
        campaign.metaAdSets?.forEach((adSet) => {
          if (!adSet) return;
          adSet.metaAds?.forEach((ad) => {
            if (!ad) return;
            ad.metaAdMetaAdCreatives?.forEach((creative) => {
              if (!creative) return;
              if (creative.metaAdCreative?.sourceInstagramMediaId === content?.postExternalId) {
                currentAdsWithContext.push({ ad, adSet, campaign });
              }
            });
          });
        });
      });
    });
    return currentAdsWithContext;
  }, [availableMetaAdAccounts, content?.postExternalId]);

  const viewMode = useMemo(() => {
    if (!availableMetaAdAccounts || availableMetaAdAccounts.length === 0) {
      return 'viewNoAdAccounts';
    }
    if (currentAds?.length > 0) {
      return 'viewCurrent';
    }
    return 'viewNoAds';
  }, [currentAds, availableMetaAdAccounts]);

  // Get alert props and boost eligibility
  const alertPropsWithEligibility = contentPreviewAlertProps(
    content?.advertisableMediaPermissions,
    content?.eligibilityErrors,
    content?.metadata?.platform,
    content?.postExternalId,
    availableMetaAdAccounts,
    availableInstagramAccounts,
  );

  /* ----------------------------- Profile Link helpers ----------------------------- */
  const getProfileUrl = (platform: string, handle: string) => {
    switch (platform) {
      case 'instagram':
        return `https://www.instagram.com/${handle}`;
      case 'tiktok':
        return `https://www.tiktok.com/@${handle}`;
      case 'youtube':
        // Handle different YouTube handle formats
        if (handle.startsWith('UC') && handle.length === 24) {
          // Channel ID
          return `https://youtube.com/channel/${handle}`;
        }
        if (handle.startsWith('http')) {
          return handle; // full URL
        }
        return `https://youtube.com/${handle}`;
      case 'facebook':
        return `https://www.facebook.com/${handle}`;
      case 'pinterest':
        return `https://www.pinterest.com/${handle}`;
      default:
        return null;
    }
  };

  const handleProfileClick = () => {
    if (!content) return;
    const profileUrl = getProfileUrl(content.metadata.platform, content.creator.handle);
    if (profileUrl) {
      window.open(profileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Memoize PostInsights transformation to avoid recalculating on every render
  const postInsights = useMemo(() => (content ? transformToPostInsights(content) : null), [content]);

  /* ----------------------------- Boost label helpers ----------------------------- */
  const advertisableMediaPermissions = content?.advertisableMediaPermissions;
  const boostLabel = useMemo(() => {
    if (!content || advertisableMediaPermissions === undefined) {
      return 'Checking permissions…';
    }
    return currentAds.length > 0 ? 'Create Another Boost' : 'Boost Post';
  }, [content, advertisableMediaPermissions, currentAds.length]);

  const canBoost = alertPropsWithEligibility.canBoost;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children && <SheetTrigger asChild>{children}</SheetTrigger>}
      <SheetOverlay className="z-[190]" />
      <SheetContent
        className="w-full overflow-y-auto p-0 z-[200] sm:max-w-[1000px] [&>button:first-of-type]:hidden flex flex-col"
        side="right"
      >
        <div className="flex flex-col h-full flex-1">
          <SheetHeaderWithClose
            title="Content Preview"
            onClose={() => onOpenChange(false)}
            closeAriaLabel="Close preview"
          />

          {/* NEW REFACTORED SheetTopBar using composable components */}
          <SheetTopBar>
            <div className="flex items-center gap-2">
              {content && !isLoading ? (
                <>
                  <SheetTopBarPlatformInfo
                    platform={content.metadata?.platform || 'instagram'}
                    clickable
                    onClick={handleProfileClick}
                    username={content.creator.handle}
                  />
                  <SheetTopBarDateBadge date={content.createdAt} />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-6 w-6 rounded" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-6 w-20 rounded-md" />
                </>
              )}
            </div>

            {content && !isLoading ? (
              <SheetTopBarBoostActionMenu
                onDownload={() => handleActionMenuClick('download-posts')}
                onViewPost={() => handleActionMenuClick('view-post')}
                onCreateBoost={() => handleActionMenuClick('create-boost')}
                canBoost={canBoost}
                boostLabel={boostLabel}
              />
            ) : (
              <Skeleton className="h-8 w-8 rounded-md" />
            )}
          </SheetTopBar>

          <SheetGrid>
            {/* Left column - Creator, Content and Caption */}
            <SheetColumn className="md:col-span-1 flex flex-col h-full">
              <PreviewSheetContent className="flex-1">
                {/* Profile Section */}
                <ProfileSection>
                  {isLoading || !content ? (
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-11 w-11 rounded-full" />
                      <div className="flex flex-col gap-1 flex-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <ProfileAvatar
                        src={content.creator.avatarUrl}
                        alt={content.creator.name}
                        name={content.creator.name}
                      />
                      <div className="flex flex-col">
                        <ProfileHandle
                          handle={content.creator.handle}
                          platform={content.metadata.platform}
                          onProfileClick={handleProfileClick}
                        />
                        <ProfileMetadataRow icon={<UserIcon className="h-3 w-3 text-grey-5" />}>
                          {content.creator.name}
                        </ProfileMetadataRow>
                      </div>
                    </div>
                  )}
                </ProfileSection>

                {postInsights ? (
                  <ContentPreviewCard
                    post={postInsights}
                    onDownload={
                      content ? () => handleDownload(content, () => handleActionMenuClick('download-posts')) : undefined
                    }
                    onOpenExternal={content ? () => handleExternalLink(content, showErrorMessage) : undefined}
                  />
                ) : (
                  <Skeleton className="h-[400px] w-full rounded-lg" />
                )}
              </PreviewSheetContent>
            </SheetColumn>

            {/* Right column - Tabs, Metrics and Details */}
            <SheetColumn className="md:col-span-2">
              <Tabs defaultValue="overview" className="w-full h-full flex flex-col">
                <div className="border-b mt-3">
                  <TabsList className="border-0 bg-transparent m-0">
                    <TabsTrigger
                      value="overview"
                      className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                    >
                      Overview
                    </TabsTrigger>
                    <TabsTrigger
                      value="boost"
                      className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                    >
                      Boost
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="overview" className="mt-4">
                  <Accordion type="multiple" defaultValue={['performance', 'info', 'additional']} className="space-y-4">
                    <AccordionItem value="performance" className="">
                      <AccordionTrigger className="p-0 hover:no-underline">
                        <PerformanceHeader className="w-full">
                          <PerformanceTitle>Post Performance</PerformanceTitle>
                          <PerformanceDateRange>
                            {content.metrics.metadata.startDate && content.metrics.metadata.endDate && (
                              <>
                                {dayjs(content.metrics.metadata.startDate).format('MMM D, YYYY')} -{' '}
                                {dayjs(content.metrics.metadata.endDate).format('MMM D, YYYY')}
                              </>
                            )}
                          </PerformanceDateRange>
                        </PerformanceHeader>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <MetricsGrid>
                          <MetricCard className="border-[#4F46E5]">
                            <MetricCardLabel>Impressions</MetricCardLabel>
                            <MetricCardValue>{compactFormatter.format(content.metrics.impressions)}</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#E11D48]">
                            <MetricCardLabel>Engagement</MetricCardLabel>
                            <MetricCardValue>{compactFormatter.format(content.metrics.engagement)}</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#EC4899]">
                            <MetricCardLabel>Engagement %</MetricCardLabel>
                            <MetricCardValue>{content.metrics.engagementRate.toFixed(1)}%</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#059669]">
                            <MetricCardLabel>TMV</MetricCardLabel>
                            <MetricCardValue>{currencyFormatter.format(content.metrics.tmv ?? 0)}</MetricCardValue>
                          </MetricCard>
                        </MetricsGrid>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="info" className="">
                      <AccordionTrigger className="p-0 hover:no-underline">
                        <h3 className="text-md font-medium">Info</h3>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <div className="space-y-4">
                          {/* Post Caption Section */}
                          <PreviewSheetSection>
                            <PreviewSheetLabel>Post Caption</PreviewSheetLabel>
                            {content.caption ? (
                              <div className="text-sm whitespace-pre-wrap">{content.caption}</div>
                            ) : (
                              <PreviewSheetEmpty />
                            )}
                          </PreviewSheetSection>

                          <PreviewSheetSection>
                            <PreviewSheetLabel>Projects</PreviewSheetLabel>
                            <PreviewSheetBadgeContainer>
                              {content.projectEditingState?.isEditing ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Select
                                    value={content.projectEditingState.projectId?.toString() ?? ''}
                                    onValueChange={(value) => {
                                      if (availableProjects) {
                                        const selectedProject = availableProjects.find(
                                          (p) => p.id.toString() === value,
                                        );
                                        if (selectedProject && content.projectEditingState) {
                                          // Just update the state, don't save yet
                                          content.projectEditingState.updateState?.({
                                            projectId: selectedProject.id.toString(),
                                            projectName: selectedProject.name,
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue placeholder="Select a project" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[1000]">
                                      {projectOptions.map((project) => (
                                        <SelectItem key={project.value} value={project.value}>
                                          {project.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (
                                        content.projectEditingState?.onSave &&
                                        content.projectEditingState.projectId &&
                                        content.projectEditingState.projectName
                                      ) {
                                        content.projectEditingState.onSave(
                                          content.projectEditingState.projectId,
                                          content.projectEditingState.projectName,
                                        );
                                      }
                                    }}
                                  >
                                    <CheckIcon className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (content.projectEditingState?.onCancel) {
                                        content.projectEditingState.onCancel();
                                      }
                                    }}
                                  >
                                    <XmarkIcon className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  {content.metadata.project ? (
                                    <PreviewSheetBadge className="bg-primaryLight hover:bg-primaryLight">
                                      {content.metadata.project.name}
                                    </PreviewSheetBadge>
                                  ) : (
                                    <PreviewSheetEmpty />
                                  )}
                                  {content.onEditProject && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={content.onEditProject}
                                    >
                                      <PencilIcon className="h-4 w-4 text-gray-900" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </PreviewSheetBadgeContainer>
                          </PreviewSheetSection>

                          <PreviewSheetSection>
                            <PreviewSheetLabel>Groups</PreviewSheetLabel>
                            <PreviewSheetBadgeContainer>
                              {content.groupEditingState?.isEditing ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Select
                                    value={content.groupEditingState.groupId?.toString() ?? ''}
                                    onValueChange={(value) => {
                                      if (availableGroups) {
                                        const selectedGroup = availableGroups.find((g) => g.id.toString() === value);
                                        if (selectedGroup && content.groupEditingState) {
                                          // Just update the state, don't save yet
                                          content.groupEditingState.updateState?.({
                                            groupId: selectedGroup.id.toString(),
                                            groupName: selectedGroup.name,
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue placeholder="Select a group" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[1000]">
                                      {groupOptions.map((group) => (
                                        <SelectItem key={group.value} value={group.value}>
                                          {group.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (
                                        content.groupEditingState?.onSave &&
                                        content.groupEditingState.groupId &&
                                        content.groupEditingState.groupName
                                      ) {
                                        content.groupEditingState.onSave(
                                          content.groupEditingState.groupId,
                                          content.groupEditingState.groupName,
                                        );
                                      }
                                    }}
                                  >
                                    <CheckIcon className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={content.groupEditingState?.onCancel}
                                  >
                                    <XmarkIcon className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  {content.metadata.group ? (
                                    <PreviewSheetBadge className="bg-secondaryLight hover:bg-secondaryLight">
                                      {content.metadata.group.name}
                                    </PreviewSheetBadge>
                                  ) : (
                                    <PreviewSheetEmpty />
                                  )}
                                  {content.onEditGroup && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={content.onEditGroup}
                                    >
                                      <PencilIcon className="h-4 w-4 text-gray-900" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </PreviewSheetBadgeContainer>
                          </PreviewSheetSection>

                          <PreviewSheetSection>
                            <PreviewSheetLabel>Last Updated</PreviewSheetLabel>
                            <div className="text-sm">
                              {new Date(content.updatedAt || content.createdAt).toLocaleDateString()}
                            </div>
                          </PreviewSheetSection>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </TabsContent>
                <TabsContent value="boost" className="mt-4 flex flex-col h-full">
                  {boostView === 'view' && (
                    <div className="flex flex-col flex-1 gap-4">
                      <ContentPreviewAlert {...alertPropsWithEligibility} />
                      {/* EPD-1676: Show success message in view mode after successful ad creation */}
                      {viewModeSuccessMessage && (
                        <InlineSuccessNotification
                          message={viewModeSuccessMessage}
                          onDismiss={() => setViewModeSuccessMessage('')}
                        />
                      )}
                      {viewMode === 'viewNoAds' && (
                        <>
                          <div>
                            <h3 className="text-md font-medium text-gray-900">
                              Create Partnership Ads by Boosting an existing post
                            </h3>
                            <p className="text-sm text-gray-600 mb-0">
                              Boost creator posts as Meta ads to reach wider audiences using branded content.
                            </p>
                          </div>
                        </>
                      )}
                      {viewMode === 'viewNoAdAccounts' && (
                        <div className="space-y-4">
                          <div className="flex flex-col gap-2">
                            <div>
                              <h3 className="text-md font-medium text-gray-900">No Ad Accounts Found</h3>
                              <p className="text-sm text-gray-600 mb-0">
                                To continue, please connect at least one Meta Ads account. Add your account here.
                              </p>
                            </div>
                            <div>
                              <Link to={`/settings/${SOCIAL_POST_APP_ID}`}>
                                <Button variant="primary" size="sm" className="flex items-center gap-2">
                                  <MetaIcon className="h-4 w-4 text-white" />
                                  Add Ad Account
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      )}
                      {viewMode === 'viewCurrent' && (
                        <div className="space-y-4">
                          <h3 className="text-md font-medium text-gray-900">Boosted Post</h3>
                          <div className="space-y-3">
                            {currentAds.map(({ ad, adSet, campaign }) => {
                              return (
                                <Card key={ad.id} className="p-4 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Badge className={cn('text-xs', getStatusBadgeColors(ad.status))}>
                                        {convertToTitleCase(ad.status)}
                                      </Badge>
                                      {ad.updatedAt && (
                                        <span className="text-xs font-normal text-grey-4">
                                          Last updated: {dayjs(ad.updatedAt).format('MMM D, YYYY')}
                                        </span>
                                      )}
                                      {!ad.updatedAt && ad.createdAt && (
                                        <span className="text-xs font-normal text-grey-4">
                                          Created: {dayjs(ad.createdAt).format('MMM D, YYYY')}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      {adSet?.stopTime ? (
                                        <span className="text-sm font-normal text-grey-5">
                                          End Date: {dayjs(adSet.stopTime).format('MMM D, YYYY')}
                                        </span>
                                      ) : (
                                        <span className="text-sm font-normal text-grey-5">Ongoing</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <BrowserIcon className="h-3.5 w-3.5 text-teal-4" />
                                    <span className="text-sm font-medium text-grey-6">{ad.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <FolderBookmarkIcon className="h-3.5 w-3.5 text-blue-5" />
                                    <span className="text-sm font-normal text-grey-5">{adSet?.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Grid2Icon className="h-3.5 w-3.5 text-lime-5" />
                                    <span className="text-sm font-normal text-grey-5">{campaign?.name}</span>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      disabled
                                      variant="outline"
                                      className="flex items-center gap-2 hidden"
                                    >
                                      <PencilIcon className="h-3.5 w-3.5" />
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      disabled
                                      variant="outline"
                                      className="flex items-center gap-2 hidden"
                                    >
                                      <ChartBarIcon className="h-3.5 w-3.5" />
                                      Track Ad
                                    </Button>
                                    <a
                                      href={`https://adsmanager.facebook.com/adsmanager/manage/ads?act=${ad.adAccountId}&selected_campaign_ids=${campaign?.id}&selected_adset_ids=${adSet?.id}&selected_ad_ids=${ad.id}&nav_source=fb4b`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={cn(
                                        buttonVariants({ variant: 'outline', size: 'sm' }),
                                        'ml-auto',
                                        'flex items-center gap-2',
                                      )}
                                    >
                                      <ArrowUpRightFromSquareIcon className="h-3.5 w-3.5" />
                                      Open in Meta
                                    </a>
                                  </div>
                                </Card>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div>
                        <Button
                          onClick={() => {
                            setBoostView('create');
                            // Clear any existing success message when switching to create mode
                            setViewModeSuccessMessage('');
                          }}
                          variant="primary"
                          size="sm"
                          className="flex items-center gap-2"
                          disabled={!alertPropsWithEligibility.canBoost}
                        >
                          <MetaIcon className="h-4 w-4 text-white" />
                          {/* Show different text based on loading state */}
                          {content.advertisableMediaPermissions === undefined ? (
                            <span>Checking permissions...</span>
                          ) : currentAds.length <= 0 ? (
                            <span>Boost Post</span>
                          ) : (
                            <span>Create Another Boost</span>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {boostView === 'create' && (
                    <Form {...boostPostForm}>
                      {/* Add inline success notification above the form */}
                      {inlineSuccessMessage && (
                        <InlineSuccessNotification
                          message={inlineSuccessMessage}
                          onDismiss={() => setInlineSuccessMessage('')}
                        />
                      )}

                      {/* Publish Confirmation Dialog */}
                      <Dialog open={showPublishConfirmation} onOpenChange={setShowPublishConfirmation}>
                        <DialogContent className="z-[300]">
                          <DialogHeader>
                            <DialogTitle>Publish Ad</DialogTitle>
                            <DialogDescription>
                              When you publish your ad, Meta will review it. After their approval, the ad will go live.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => setShowPublishConfirmation(false)}
                              disabled={isBoostLoading}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => {
                                const formValues = boostPostForm.getValues();
                                onBoostPostSubmit(formValues);
                              }}
                              disabled={isBoostLoading}
                            >
                              {isBoostLoading ? 'Publishing...' : 'Publish'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      <form
                        className="flex flex-col flex-1"
                        onSubmit={boostPostForm.handleSubmit(() => {
                          // Only open confirmation dialog if validation passes
                          setShowPublishConfirmation(true);
                        }, onBoostPostError)}
                      >
                        <div className="flex-1 space-y-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-md font-medium text-gray-900">Publish Post</h3>
                            <FormField
                              control={boostPostForm.control}
                              name="adAccount.id"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Select value={field.value} onValueChange={field.onChange}>
                                      <SelectTrigger className="w-[200px]">
                                        <SelectValue placeholder="Select Ad Account" />
                                      </SelectTrigger>
                                      <SelectContent className="z-[1000]">
                                        {metaAdAccountOptions.map((metaAdAccount) => (
                                          <SelectItem key={metaAdAccount.value} value={metaAdAccount.value}>
                                            {metaAdAccount.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <div className="rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <FolderBookmarkIcon className="h-3.5 w-3.5 text-blue-5" />
                                <h4 className="font-medium m-0 p-0 text-gray-900">Campaigns</h4>
                              </div>
                              {campaignType === 'new' ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-1"
                                  onClick={() => boostPostForm.setValue('campaign.type', 'existing')}
                                  type="button"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                  <span>Select Existing Campaign</span>
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-1"
                                  onClick={() => boostPostForm.setValue('campaign.type', 'new')}
                                  type="button"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                  <span>New Campaign</span>
                                </Button>
                              )}
                            </div>
                            <div className="space-y-4">
                              {campaignType === 'existing' ? (
                                <FormField
                                  control={boostPostForm.control}
                                  name="campaign.campaignId"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Select Campaign</FormLabel>
                                      <FormControl>
                                        <Select
                                          value={field.value}
                                          onValueChange={field.onChange}
                                          defaultValue={field.value}
                                        >
                                          <SelectTrigger id="campaign-select" className="w-full">
                                            <SelectValue placeholder="Choose an existing campaign" />
                                          </SelectTrigger>
                                          <SelectContent className="z-[1000]">
                                            {campaignOptions.map((campaign) => (
                                              <SelectItem key={campaign.value} value={campaign.value}>
                                                {campaign.label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              ) : (
                                <>
                                  <FormField
                                    control={boostPostForm.control}
                                    name="campaign.name"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Campaign Name</FormLabel>
                                        <FormControl>
                                          <Input
                                            placeholder="Enter campaign name"
                                            {...field}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                              }
                                            }}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={boostPostForm.control}
                                    name="campaign.objective"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Objective</FormLabel>
                                        <FormControl>
                                          <Select
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            defaultValue={field.value}
                                          >
                                            <SelectTrigger id="new-campaign-objective" className="w-full">
                                              <SelectValue placeholder="Select objective" />
                                            </SelectTrigger>
                                            <SelectContent className="z-[1000]">
                                              {CAMPAIGN_OBJECTIVES.map((obj: { value: string; label: string }) => (
                                                <SelectItem key={obj.value} value={obj.value}>
                                                  {obj.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                          <div />
                          <div className="rounded-lg border border-gray-200 p-4 flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Grid2Icon className="h-3.5 w-3.5 text-lime-4" />
                                <h4 className="font-medium m-0 p-0 text-gray-900">Ad Set</h4>
                              </div>
                              {!isCampaignValid && <ChevronRightIcon className="h-4 w-4 text-gray-600 disabled" />}
                              {isCampaignValid &&
                                (adSetType === 'existing' ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-1"
                                    onClick={() => boostPostForm.setValue('adSet.type', 'new')}
                                    type="button"
                                    disabled={campaignType === 'new'} // EPD-1486: Disable when creating new campaign
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    <span>New Ad Set</span>
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-1"
                                    onClick={() => boostPostForm.setValue('adSet.type', 'existing')}
                                    type="button"
                                    disabled={campaignType === 'new'} // EPD-1486: Disable when creating new campaign
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    <span>Select Existing Ad Set</span>
                                  </Button>
                                ))}
                            </div>
                            {/* EPD-1486: Show helper text when campaign is new */}
                            {isCampaignValid && campaignType === 'new' && (
                              <Alert className="border-blue-200 bg-blue-50 text-blue-800">
                                <AlertDescription>
                                  New campaigns require a new ad set since no existing ad sets are available.
                                </AlertDescription>
                              </Alert>
                            )}
                            {isCampaignValid && (
                              <div className="space-y-4">
                                {adSetType === 'existing' ? (
                                  <FormField
                                    control={boostPostForm.control}
                                    name="adSet.id"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Select Ad Set</FormLabel>
                                        <FormControl>
                                          <Select
                                            value={field.value}
                                            onValueChange={(value) => {
                                              field.onChange(value);
                                            }}
                                            defaultValue={field.value}
                                          >
                                            <SelectTrigger id="adset-select" className="w-full">
                                              <SelectValue placeholder="Choose an existing ad set" />
                                            </SelectTrigger>
                                            <SelectContent className="z-[1000]">
                                              {availableAdSets
                                                ?.filter((adSet): adSet is NonNullable<typeof adSet> => !!adSet)
                                                ?.map((adSet) => {
                                                  return (
                                                    <SelectItem key={adSet.id} value={adSet.id}>
                                                      {adSet.name}
                                                    </SelectItem>
                                                  );
                                                })}
                                            </SelectContent>
                                          </Select>
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                ) : (
                                  <>
                                    <FormField
                                      control={boostPostForm.control}
                                      name="adSet.name"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Ad Set Name</FormLabel>
                                          <FormControl>
                                            <Input
                                              placeholder="Enter ad set name"
                                              {...field}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  e.preventDefault();
                                                }
                                              }}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <div className="flex gap-2">
                                      <FormItem>
                                        <FormLabel>Budget Type</FormLabel>
                                        <Select
                                          onValueChange={(value: 'lifetime' | 'daily') => {
                                            if (value === 'lifetime') {
                                              boostPostForm.setValue('adSet.dailyBudget', undefined);
                                            } else {
                                              boostPostForm.setValue('adSet.lifetimeBudget', undefined);
                                            }
                                            setSelectedBudgetType(value);
                                          }}
                                          defaultValue={selectedBudgetType}
                                        >
                                          <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Select budget type" />
                                          </SelectTrigger>
                                          <SelectContent className="z-[1000]">
                                            <SelectItem value="daily">Daily Budget</SelectItem>
                                            <SelectItem value="lifetime">Lifetime Budget</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </FormItem>

                                      {/* Conditionally render Lifetime Budget */}

                                      {selectedBudgetType === 'lifetime' && (
                                        <FormField
                                          control={boostPostForm.control}
                                          name="adSet.lifetimeBudget"
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Lifetime Budget</FormLabel>
                                              <FormControl>
                                                <div className="relative">
                                                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                    <span className="text-gray-500 sm:text-sm">$</span>
                                                  </div>
                                                  <Input
                                                    type="number"
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="0.01"
                                                    className="pl-7"
                                                    value={field.value ?? ''}
                                                    onChange={(e) => {
                                                      const val = e.target.value;
                                                      field.onChange(val === '' ? undefined : parseFloat(val));
                                                      if (val !== '') {
                                                        boostPostForm.setValue('adSet.dailyBudget', undefined);
                                                      }
                                                    }}
                                                  />
                                                </div>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      )}

                                      {/* Conditionally render Daily Budget */}
                                      {selectedBudgetType === 'daily' && (
                                        <FormField
                                          control={boostPostForm.control}
                                          name="adSet.dailyBudget"
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Daily Budget</FormLabel>
                                              <FormControl>
                                                <div className="relative">
                                                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                    <span className="text-gray-500 sm:text-sm">$</span>
                                                  </div>
                                                  <Input
                                                    type="number"
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="0.01"
                                                    className="pl-7"
                                                    value={field.value ?? ''}
                                                    onChange={(e) => {
                                                      const val = e.target.value;
                                                      field.onChange(val === '' ? undefined : parseFloat(val));
                                                      if (val !== '') {
                                                        boostPostForm.setValue('adSet.lifetimeBudget', undefined);
                                                      }
                                                    }}
                                                  />
                                                </div>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <FormField
                                        control={boostPostForm.control}
                                        name="adSet.startTime"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel>Start Date</FormLabel>
                                            <FormControl>
                                              <Input
                                                type="datetime-local"
                                                value={toLocalDatetimeInputValue(field.value)}
                                                onChange={(e) =>
                                                  field.onChange(e.target.value ? e.target.valueAsDate ?? undefined : undefined)
                                                }
                                              />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                    </div>
                                    <div>
                                      <div className="flex items-center space-x-2 mb-3">
                                        <Checkbox
                                          id="has-end-date"
                                          checked={hasEndDate}
                                          onCheckedChange={(checked) => {
                                            setHasEndDate(checked as boolean);
                                            if (!checked) {
                                              boostPostForm.setValue('adSet.stopTime', undefined);
                                            }
                                          }}
                                        />
                                        <FormLabel htmlFor="has-end-date" className="">
                                          End Date
                                        </FormLabel>
                                      </div>
                                      <div>
                                        {hasEndDate && (
                                          <FormField
                                            control={boostPostForm.control}
                                            name="adSet.stopTime"
                                            render={({ field }) => (
                                              <FormItem>
                                                <FormControl>
                                                  <Input
                                                    type="datetime-local"
                                                    value={toLocalDatetimeInputValue(field.value)}
                                                    onChange={(e) =>
                                                      field.onChange(e.target.value ? e.target.valueAsDate ?? undefined : undefined)
                                                    }
                                                  />
                                                </FormControl>
                                                <FormMessage />
                                              </FormItem>
                                            )}
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <FormField
                                      control={boostPostForm.control}
                                      name="adSet.targeting.geo_locations.countries"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Target Countries</FormLabel>
                                          <FormControl>
                                            <MultiSelect
                                              value={field.value || []}
                                              onChange={field.onChange}
                                              options={COUNTRY_DATA.map((country) => ({
                                                label: country.countryName,
                                                value: country.countryShortCode,
                                              }))}
                                              placeholder="Select countries"
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={boostPostForm.control}
                                      name="adSet.billingEvent"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Billing Event (Optional)</FormLabel>
                                          <FormControl>
                                            <Select
                                              value={field.value}
                                              onValueChange={field.onChange}
                                              defaultValue={field.value}
                                            >
                                              <SelectTrigger className="w-full">
                                                <SelectValue placeholder="Select billing event" />
                                              </SelectTrigger>
                                              <SelectContent className="z-[1000]">
                                                {[
                                                  'APP_INSTALLS',
                                                  'CLICKS',
                                                  'IMPRESSIONS',
                                                  'LINK_CLICKS',
                                                  'LISTING_INTERACTION',
                                                  'OFFER_CLAIMS',
                                                  'PAGE_LIKES',
                                                  'POST_ENGAGEMENT',
                                                  'PURCHASE',
                                                  'THRUPLAY',
                                                  'VIDEO_VIEWS',
                                                ].map((event) => (
                                                  <SelectItem key={event} value={event}>
                                                    {event
                                                      .replace(/_/g, ' ')
                                                      .toLowerCase()
                                                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />

                                    <FormField
                                      control={boostPostForm.control}
                                      name="adSet.bidAmount"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Bid Amount (Optional)</FormLabel>
                                          <FormControl>
                                            <div className="relative">
                                              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                <span className="text-gray-500 sm:text-sm">$</span>
                                              </div>
                                              <Input
                                                type="number"
                                                placeholder="0.00"
                                                min="0"
                                                step="0.01"
                                                className="pl-7"
                                                {...field}
                                                value={field.value ?? ''}
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  field.onChange(val === '' ? undefined : parseFloat(val));
                                                }}
                                              />
                                            </div>
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="rounded-lg border border-gray-200 p-4 flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <BrowserIcon className="h-3.5 w-3.5 text-teal-4" />
                                <h4 className="font-medium m-0 p-0 text-gray-900">Configure Ad</h4>
                              </div>
                              {!isCampaignValid && <ChevronRightIcon className="h-4 w-4 text-gray-600 disabled" />}
                            </div>
                            {isCampaignValid && (
                              <div className="space-y-4">
                                <FormField
                                  control={boostPostForm.control}
                                  name="ad.name"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Ad Name</FormLabel>
                                      <FormControl>
                                        <Input
                                          placeholder="Enter ad name"
                                          {...field}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                            }
                                          }}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={boostPostForm.control}
                                  name="ad.callToActionType"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Call to Action</FormLabel>
                                      <FormControl>
                                        <Select
                                          value={field.value}
                                          onValueChange={field.onChange}
                                          defaultValue={field.value}
                                        >
                                          <SelectTrigger id="adset-action" className="w-full">
                                            <SelectValue placeholder="Choose a call to action" />
                                          </SelectTrigger>
                                          <SelectContent className="z-[1000]">
                                            {[
                                              'ADD_TO_CART',
                                              'APPLY_NOW',
                                              'ASK_ABOUT_SERVICES',
                                              'ASK_FOR_MORE_INFO',
                                              'AUDIO_CALL',
                                              'BOOK_A_CONSULTATION',
                                              'BOOK_NOW',
                                              'BOOK_TRAVEL',
                                              'BUY',
                                              'BUY_NOW',
                                              'BUY_TICKETS',
                                              'BUY_VIA_MESSAGE',
                                              'CALL',
                                              'CALL_ME',
                                              'CALL_NOW',
                                              'CHAT_WITH_US',
                                              'CONFIRM',
                                              'CONTACT',
                                              'CONTACT_US',
                                              'DONATE',
                                              'DONATE_NOW',
                                              'DOWNLOAD',
                                              'EVENT_RSVP',
                                              'FIND_A_GROUP',
                                              'FIND_YOUR_GROUPS',
                                              'FOLLOW_NEWS_STORYLINE',
                                              'FOLLOW_PAGE',
                                              'FOLLOW_USER',
                                              'GET_A_QUOTE',
                                              'GET_DIRECTIONS',
                                              'GET_IN_TOUCH',
                                              'GET_OFFER',
                                              'GET_OFFER_VIEW',
                                              'GET_PROMOTIONS',
                                              'GET_QUOTE',
                                              'GET_SHOWTIMES',
                                              'GET_STARTED',
                                              'INQUIRE_NOW',
                                              'INSTAGRAM_MESSAGE',
                                              'INSTALL_APP',
                                              'INSTALL_MOBILE_APP',
                                              'JOIN_CHANNEL',
                                              'LEARN_MORE',
                                              'LIKE_PAGE',
                                              'LISTEN_MUSIC',
                                              'LISTEN_NOW',
                                              'MAKE_AN_APPOINTMENT',
                                              'MESSAGE_PAGE',
                                              'MOBILE_DOWNLOAD',
                                              'NO_BUTTON',
                                              'OPEN_INSTANT_APP',
                                              'OPEN_LINK',
                                              'ORDER_NOW',
                                              'PAY_TO_ACCESS',
                                              'PLAY_GAME',
                                              'PLAY_GAME_ON_FACEBOOK',
                                              'PURCHASE_GIFT_CARDS',
                                              'RAISE_MONEY',
                                              'RECORD_NOW',
                                              'REFER_FRIENDS',
                                              'REQUEST_TIME',
                                              'SAY_THANKS',
                                              'SEE_MORE',
                                              'SELL_NOW',
                                              'SEND_A_GIFT',
                                              'SEND_GIFT_MONEY',
                                              'SEND_UPDATES',
                                              'SHARE',
                                              'SHOP_NOW',
                                              'SIGN_UP',
                                              'SOTTO_SUBSCRIBE',
                                              'START_ORDER',
                                              'SUBSCRIBE',
                                              'SWIPE_UP_PRODUCT',
                                              'SWIPE_UP_SHOP',
                                              'UPDATE_APP',
                                              'USE_APP',
                                              'USE_MOBILE_APP',
                                              'VIDEO_ANNOTATION',
                                              'VIDEO_CALL',
                                              'VIEW_CART',
                                              'VIEW_CHANNEL',
                                              'VIEW_INSTAGRAM_PROFILE',
                                              'VIEW_IN_CART',
                                              'VIEW_PRODUCT',
                                              'VISIT_PAGES_FEED',
                                              'WATCH_LIVE_VIDEO',
                                              'WATCH_MORE',
                                              'WATCH_VIDEO',
                                              'WHATSAPP_MESSAGE',
                                              'WOODHENGE_SUPPORT',
                                            ].map((option) => (
                                              <SelectItem key={option} value={option}>
                                                {option
                                                  .replace(/_/g, ' ')
                                                  .toLowerCase()
                                                  .replace(/\b\w/g, (c) => c.toUpperCase())}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={boostPostForm.control}
                                  name="ad.identityDisplayMode"
                                  render={({ field }) => (
                                    <FormItem>
                                      <div className="flex items-center gap-2">
                                        <FormLabel>Identity</FormLabel>
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <CircleInfoIcon className="h-4 w-4 text-gray-400 cursor-help" />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              Select which social handle to display at the top of this ad.
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      </div>
                                      <FormControl>
                                        <Select
                                          value={field.value}
                                          onValueChange={field.onChange}
                                          defaultValue={field.value}
                                        >
                                          <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Select an identity to display in the header">
                                              {field.value === 'CREATOR_AND_BRAND' && 'Creator and Brand'}
                                              {field.value === 'CREATOR_ONLY' && 'Creator Only'}
                                              {field.value === 'DYNAMIC' && 'Dynamic (Instagram Only)'}
                                            </SelectValue>
                                          </SelectTrigger>
                                          <SelectContent
                                            className="z-[1000]"
                                            style={{ width: 'var(--radix-select-trigger-width)' }}
                                          >
                                            <SelectItem value="CREATOR_AND_BRAND">
                                              <div>
                                                <div>Creator and Brand</div>
                                                <div className="text-sm text-gray-500">
                                                  [@creatorhandle] with [@brandhandle]
                                                </div>
                                              </div>
                                            </SelectItem>
                                            <SelectItem value="CREATOR_ONLY">
                                              <div>
                                                <div>Creator Only</div>
                                                <div className="text-sm text-gray-500">[@creatorhandle]</div>
                                              </div>
                                            </SelectItem>
                                            <SelectItem value="DYNAMIC">
                                              <div>
                                                <div>Dynamic (Instagram Only)</div>
                                                <div className="text-sm text-gray-500">
                                                  Allows Meta to automatically optimize for the most performant option
                                                  between single identity and dual identity.
                                                </div>
                                              </div>
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                {/* EPD-1644: Only show Destination URL field when callToActionType is not NO_BUTTON */}
                                {callToActionType !== 'NO_BUTTON' && (
                                  <FormField
                                    control={boostPostForm.control}
                                    name="ad.callToActionUrl"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Destination URL</FormLabel>
                                        <FormControl>
                                          <Input
                                            placeholder="Creators promotional page"
                                            {...field}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                              }
                                            }}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                )}
                                {/* EPD-1644: Only show UTM Parameters when callToActionType is not NO_BUTTON */}
                                {callToActionType !== 'NO_BUTTON' && (
                                  <GenericUtmParametersGroup
                                    control={boostPostForm.control}
                                    watch={boostPostForm.watch}
                                    setValue={boostPostForm.setValue}
                                    fieldPaths={{
                                      useUtmParameters: 'ad.useUtmParameters',
                                      utmParameters: {
                                        utmSource: 'ad.utmParameters.utmSource',
                                        utmMedium: 'ad.utmParameters.utmMedium',
                                        utmCampaign: 'ad.utmParameters.utmCampaign',
                                        utmContent: 'ad.utmParameters.utmContent',
                                        utmTerm: 'ad.utmParameters.utmTerm',
                                      },
                                      customUtmParameters: 'ad.customUtmParameters',
                                      targetUrl: 'ad.callToActionUrl',
                                    }}
                                    urlPreview={previewUrl}
                                    title="UTM Parameters"
                                    description="Add UTM parameters to track ad performance and attribution"
                                    dynamicOptions={DYNAMIC_UTM_OPTIONS}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Footer actions section */}
                        <div className="flex items-center justify-start gap-3 pt-4 border-t border-gray-200 mt-auto">
                          <Button
                            variant="outline"
                            onClick={onBoostPostCancel}
                            className="w-24"
                            type="button"
                            disabled={isBoostLoading}
                          >
                            Cancel
                          </Button>
                          <Button className="w-24" type="submit" disabled={isBoostLoading}>
                            {isBoostLoading ? 'Publishing...' : 'Publish'}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  )}
                </TabsContent>
              </Tabs>
            </SheetColumn>
          </SheetGrid>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ContentPreviewSheetBoost;
